import { PhoneTips, AlertsToggle } from "./PhoneTips";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home, GraduationCap, FolderCheck, FlaskConical, Megaphone, Link2, Users, Settings2, Inbox, LogOut, Bell, Menu, BookOpen, TrendingUp, Network,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useAuth } from "../lib/auth";
import { api, fmtDateTime } from "../lib/api";
import { useApi } from "./kit";

function navItems(user) {
  const items = [
    { to: "/", label: "Home", icon: Home, end: true },
    { to: "/training", label: "My Training", icon: GraduationCap },
    { to: "/records", label: "My Records", icon: FolderCheck },
    { to: "/performance", label: "Performance Reviews", icon: TrendingUp },
    { to: "/org-chart", label: "Org Chart", icon: Network },
    { to: "/sds", label: "Safety Data Sheets", icon: FlaskConical },
    { to: "/concern", label: "Report a Concern", icon: Megaphone },
    { to: "/policies", label: "Policies", icon: BookOpen },
    { to: "/links", label: "Useful Links", icon: Link2 },
  ];
  if (user.is_manager || user.is_superadmin) items.push({ to: "/team", label: "My Team", icon: Users });
  const reviewer = user.is_superadmin || user.is_manager || (user.permissions || []).some((p) => !["trainer", "course_editor"].includes(p));
  if (reviewer) items.push({ to: "/reviews", label: "Review Queues", icon: Inbox });
  if (user.is_superadmin) items.push({ to: "/admin", label: "Administration", icon: Settings2 });
  else if ((user.permissions || []).includes("course_editor")) items.push({ to: "/admin/courses", label: "Course builder", icon: Settings2 });
  return items;
}

function NavList({ user, onPick, dark }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Main">
      {navItems(user).map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onPick} data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
          className={({ isActive }) =>
            `tap flex items-center gap-3 rounded-lg px-3 text-[15px] font-medium transition-colors ${
              isActive
                ? dark ? "bg-white/10 text-white" : "bg-[color:var(--ink)] text-white"
                : dark ? "text-white/70 hover:bg-white/5 hover:text-white" : "hover:bg-muted"
            }`}>
          <Icon className="h-5 w-5" aria-hidden="true" /> {label}
        </NavLink>
      ))}
    </nav>
  );
}

function Bellbox() {
  const { data, reload } = useApi("/notifications");
  const nav = useNavigate();
  const unread = (data || []).filter((n) => !n.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) api.post("/notifications/read-all").then(reload); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" aria-label={`Notifications, ${unread} unread`}
          className="relative tap w-12 inline-flex items-center justify-center rounded-lg hover:bg-muted">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notifications-unread" className="absolute right-2 top-2 min-w-[18px] rounded-full bg-[color:var(--dcbc-red)] px-1 text-[11px] font-bold text-white">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <p className="border-b px-4 py-3 font-semibold">Notifications</p>
        <div className="max-h-96 overflow-auto">
          {(data || []).length === 0 && <p className="p-4 text-sm text-muted-foreground">Nothing yet.</p>}
          {(data || []).map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} className="block w-full border-b px-4 py-3 text-left text-sm hover:bg-muted">
              <span className={n.read ? "" : "font-semibold"}>{n.text}</span>
              <span className="block text-xs text-muted-foreground mt-0.5">{fmtDateTime(n.at)}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function UserCard({ user, logout }) {
  const role = user.is_superadmin ? "Superadmin" : user.is_manager ? "Manager" : "Employee";
  return (
    <div className="rounded-xl bg-white/5 p-3">
      <p className="font-semibold text-white truncate" data-testid="current-user-name">{user.name}</p>
      <p className="text-xs text-white/60">{role} · {user.employee_id}{user.is_demo ? " · SAMPLE" : ""}</p>
      <button onClick={logout} data-testid="logout-button" className="mt-3 tap w-full inline-flex items-center justify-center gap-2 rounded-lg border border-white/15 text-sm text-white/80 hover:bg-white/10">
        <LogOut className="h-4 w-4" /> Sign out
      </button>
      <AlertsToggle />
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const bottom = [
    { to: "/", label: "Home", icon: Home, end: true },
    { to: "/training", label: "Training", icon: GraduationCap },
    { to: "/sds", label: "SDS", icon: FlaskConical },
    { to: "/concern", label: "Report", icon: Megaphone },
  ];
  return (
    <div className="min-h-screen lg:flex">
      <aside className="print:hidden hidden lg:flex lg:w-72 lg:flex-col lg:fixed lg:inset-y-0 bg-[color:var(--ink)] grain p-5">
        <div className="mb-8 flex items-center gap-3 px-1">
          <img src="/brand/dcbc_mark_white.png" alt="" className="h-11 w-auto" />
          <div>
            <p className="font-display text-xl font-semibold leading-none tracking-wide text-white">DCBC</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/60 mt-1">Workplace</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto"><NavList user={user} dark /></div>
        <UserCard user={user} logout={logout} />
      </aside>
      <div className="flex-1 lg:pl-72 print:pl-0">
        <header className="print:hidden sticky top-0 z-30 flex items-center justify-between border-b bg-background/90 backdrop-blur-md px-4 sm:px-8 h-16">
          <div className="flex items-center gap-2 lg:hidden">
            <img src="/brand/dcbc_mark_red.png" alt="" className="h-8 w-auto" />
            <span className="font-display font-semibold text-lg tracking-wide">DCBC</span>
          </div>
          <div className="hidden lg:block text-sm text-muted-foreground">
            {user.is_demo && <span data-testid="demo-banner" className="rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-900">Demo mode — signed in as a SAMPLE person</span>}
          </div>
          <div className="flex items-center gap-1">
            <Bellbox />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden tap w-12 inline-flex items-center justify-center rounded-lg hover:bg-muted" aria-label="Open menu" data-testid="mobile-menu-button">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[color:var(--ink)] border-0 p-5 flex flex-col">
                <SheetTitle className="text-white font-display">Menu</SheetTitle>
                <div className="flex-1 overflow-y-auto mt-4"><NavList user={user} dark onPick={() => setOpen(false)} /></div>
                <UserCard user={user} logout={logout} />
              </SheetContent>
            </Sheet>
          </div>
        </header>
        {user.is_demo && <p className="lg:hidden bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-900">Demo mode — SAMPLE person</p>}
        <main className="mx-auto max-w-6xl px-4 sm:px-8 py-8 pb-28 lg:pb-12">{children}</main>
        <PhoneTips />
      </div>
      <nav className="print:hidden lg:hidden fixed bottom-0 inset-x-0 z-30 grid grid-cols-4 border-t bg-white" aria-label="Quick">
        {bottom.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`bottom-nav-${label.toLowerCase()}`}
            className={({ isActive }) => `flex h-16 flex-col items-center justify-center gap-1 text-xs font-semibold ${isActive ? "text-[color:var(--dcbc-red)]" : "text-slate-600"}`}>
            <Icon className="h-5 w-5" aria-hidden="true" /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
